import React, { useState } from "react";
import PropTypes from "prop-types";
import { TableSortLabel, Typography } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box
} from "@mui/material";
import AllInboxIcon from '@mui/icons-material/AllInbox';
import "../../styles/component/table.scss"
import { useTranslation } from "react-i18next";


const TableComponent = ({
  columns,
  setPageNo,
  perPage,
  setPerPage,
  pageNo,
  totalRecords,
  children,
  setData,
  data,
  className,
  hidePagination = true
}) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  const handleChangePage = (event, newPage) => {
    setPageNo(newPage + 1);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPageNo(1);
  };

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: field, direction });

    const sortedData = [...data].sort((a, b) => {
      // Check if the field is related to dates
      if (field === 'nextPaymentDate' || field === 'subscriptionEndDate' || field === 'subscriptionStartDate') {
        const dateA = new Date(a[field]);
        const dateB = new Date(b[field]);

        // Compare the dates
        if (dateA < dateB) {
          return direction === 'asc' ? -1 : 1;
        }
        if (dateA > dateB) {
          return direction === 'asc' ? 1 : -1;
        }
      } else if (field === 'planFrequency') {
        if (a.firstBillingDetail[field] < b.firstBillingDetail[field]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a.firstBillingDetail[field] > b.firstBillingDetail[field]) {
          return direction === 'asc' ? 1 : -1;
        }
      } else if (field === 'name') {
        if (a[field] < b[field]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a[field] > b[field]) {
          return direction === 'asc' ? 1 : -1;
        }
      } else {
        // Default sorting for non-date fields
        if (a.client[field] < b.client[field]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a.client[field] > b.client[field]) {
          return direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    setData(sortedData);
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        background: "#fff",
        marginTop: "12px",
        border: "1px solid #E3E3E3",
        borderRadius: "1rem",
        overflowX: "auto",
        maxHeight: className ?? 380,
        overflowY: "auto",
      }}
      className={className ? className : ''}
    >
      <Table
        sx={{ minWidth: 900, background: "#fff" }}
        aria-label="common table"
        className="common-table"
        stickyHeader
      >
        <TableHead>
          <TableRow>
            {columns?.map((column) => (
              <TableCell
                key={column.field}
                onClick={() => column.sortable && handleSort(column.field)}
                className="table-menu"
                sx={{ fontSize: "14px", whiteSpace: 'nowrap', fontWeight: "500", cursor: column.sortable ? 'pointer' : 'default' }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortConfig.key === column.field}
                    direction={sortConfig.key === column.field ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort(column.field)}
                  >
                    {t(column.translationKey)}
                  </TableSortLabel>
                ) : (
                  t(column.translationKey)
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {
            children === undefined || data?.length === 0 || children.length <= 0 ?
              <TableRow>
                <TableCell colSpan={20} sx={{ textAlign: "center", py: 4 }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <AllInboxIcon sx={{ fontSize: 40, color: "gray", opacity: .5 }} />
                    <Typography variant="h6" color="textSecondary" sx={{ opacity: 0.5, fontSize: "18px" }}>
                      {t('common_text_no_data')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              : children
          }

          {
            hidePagination &&
            <TableRow sx={{
              padding: "0px",
              position: "sticky",
              bottom: 0,
            }} >
              <TableCell colSpan={20} sx={{ padding: "0px" }}>
                <TablePagination
                  sx={{
                    background: "#fff",
                    minWidth: 900,
                    fontFamily: "Poppins",
                    borderTop: '1px solid #e0e0e0',
                    "& .MuiTablePagination-selectLabel": {
                      fontFamily: "Poppins",
                    },
                    "& .MuiTablePagination-select": {
                      fontFamily: "Poppins",
                      margin: "0px 10px"
                    },
                    "& .MuiTablePagination-displayedRows": {
                      fontFamily: "Poppins",
                    },
                    "& .MuiTablePagination-actions": {
                      fontFamily: "Poppins",
                    },
                    "& .MuiTablePagination-menuItem": {
                      fontFamily: "Poppins",
                    },
                    "& .MuiSelect-select": {
                      padding: "0px"
                    }
                  }}
                  labelRowsPerPage={t('table_rows_per_page')}
                  rowsPerPageOptions={[25, 100, 500, 1000]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={perPage}
                  page={pageNo - 1}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableCell>
            </TableRow>
          }
          {/* {children} */}
        </TableBody>
      </Table>

      {/* Pagination */}

    </TableContainer>
  );
};

TableComponent.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
};

export default TableComponent;